/**
 * EditModuleModal — F12.A2 T12.A2.3+T12.A2.5.
 *
 * Edycja istniejącego modułu: name, description, color (preset 8), icon (IconPicker),
 * showInMenu, recordNameField (dropdown po polach modułu typu text/email/phone/url).
 *
 * Backend: PUT `/api/modules/:id` (już istniejący endpoint).
 *
 * Uruchamiany z menu kafelka modułu w `ModulesPage` ("Edytuj moduł").
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { Button } from '@/components/Button';
import { Dropdown } from '@/components/Dropdown';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { Spinner } from '@/components/Spinner';
import { Textarea } from '@/components/Textarea';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import { fetchProcessesForModule } from '@/lib/processes/instance-api';
import { useMyModules } from '@/lib/use-my-modules';

import { IconPicker } from './IconPicker';

const PRESET_COLORS = [
  '#3B82F6',
  '#10B981',
  '#F59E0B',
  '#EF4444',
  '#8B5CF6',
  '#EC4899',
  '#06B6D4',
  '#6B7280',
];

/** Typy pól które mogą służyć jako "nazwa rekordu" (czytelny krótki tekst). */
const NAME_FIELD_TYPES = new Set(['text', 'email', 'phone', 'url']);

interface ModuleField {
  id: string;
  name: string;
  slug: string;
  fieldType: string;
}

interface ModuleDetail {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  icon: string;
  color: string;
  showInMenu: boolean;
  recordNameField: string | null;
  /** T17.1.9 — domyślny proces akceptacji (F17 Procesy). */
  defaultProcessId: string | null;
  fields: ModuleField[];
}

interface EditModuleModalProps {
  moduleId: string;
  open: boolean;
  onClose: () => void;
}

interface FormState {
  name: string;
  description: string;
  icon: string;
  color: string;
  showInMenu: boolean;
  recordNameField: string;
  defaultProcessId: string;
}

export function EditModuleModal({ moduleId, open, onClose }: EditModuleModalProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<FormState | null>(null);

  const moduleQuery = useQuery({
    queryKey: ['modules', moduleId, 'detail'],
    queryFn: async () => {
      const res = await api.get<{ module: ModuleDetail }>(`/modules/${moduleId}`);
      return res.data.module;
    },
    enabled: open,
  });

  // T17.1.9 — picker domyślnego procesu: tylko gdy moduł Procesy aktywny.
  const { data: myModules } = useMyModules({ enabled: open });
  const processesActive = myModules?.subscriptions.some((s) => s.code === 'processes') ?? false;
  // Qualified kod celu — używany ZARÓWNO w queryKey, jak i w queryFn, żeby cache
  // dzielił się z `SubmitForApprovalButton` (ten sam klucz `['processes','for-module',custom.<slug>]`).
  const targetModuleCode = moduleQuery.data ? `custom.${moduleQuery.data.slug}` : undefined;
  const processesQuery = useQuery({
    queryKey: ['processes', 'for-module', targetModuleCode],
    queryFn: () => fetchProcessesForModule(targetModuleCode!),
    enabled: open && processesActive && !!targetModuleCode,
  });
  const processes = processesQuery.data ?? [];

  // Po załadowaniu modułu inicjalizujemy formularz
  useEffect(() => {
    if (moduleQuery.data && open) {
      setForm({
        name: moduleQuery.data.name,
        description: moduleQuery.data.description ?? '',
        icon: moduleQuery.data.icon,
        color: moduleQuery.data.color,
        showInMenu: moduleQuery.data.showInMenu,
        recordNameField: moduleQuery.data.recordNameField ?? '',
        defaultProcessId: moduleQuery.data.defaultProcessId ?? '',
      });
    }
    if (!open) setForm(null);
  }, [moduleQuery.data, open]);

  const updateMutation = useMutation({
    mutationFn: async (input: FormState) => {
      const res = await api.put<{ module: ModuleDetail }>(`/modules/${moduleId}`, {
        name: input.name.trim(),
        description: input.description.trim() === '' ? null : input.description.trim(),
        icon: input.icon,
        color: input.color,
        showInMenu: input.showInMenu,
        recordNameField: input.recordNameField === '' ? null : input.recordNameField,
        // T17.1.9 — defaultProcessId tylko gdy moduł Procesy aktywny (picker widoczny);
        // inaczej nie ruszamy pola (omit z body) i nie wymuszamy walidacji backendu.
        ...(processesActive
          ? { defaultProcessId: input.defaultProcessId === '' ? null : input.defaultProcessId }
          : {}),
      });
      return res.data.module;
    },
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('tenant.modules.toasts.updated') });
      queryClient.invalidateQueries({ queryKey: ['modules'] });
      queryClient.invalidateQueries({ queryKey: ['modules', 'menu'] });
      queryClient.invalidateQueries({ queryKey: ['modules', moduleId] });
      queryClient.invalidateQueries({ queryKey: ['modules', moduleId, 'detail'] });
      // Rekordy muszą się odświeżyć — backend przeliczył record_name dla wszystkich wierszy
      queryClient.invalidateQueries({ queryKey: ['module-records'] });
      onClose();
    },
    onError: (err) => {
      const message = isAxiosError(err)
        ? (err.response?.data?.error ?? t('tenant.modules.toasts.updateError'))
        : t('tenant.modules.toasts.updateError');
      toast.show({
        variant: 'error',
        title: t('tenant.modules.toasts.updateError'),
        description: String(message),
      });
    },
  });

  const handleSave = () => {
    if (!form) return;
    if (!form.name.trim()) return;
    updateMutation.mutate(form);
  };

  const nameFields = (moduleQuery.data?.fields ?? []).filter((f) =>
    NAME_FIELD_TYPES.has(f.fieldType),
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('tenant.modules.editModule.title')}
      description={t('tenant.modules.editModule.description')}
    >
      {moduleQuery.isLoading || !form ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : (
        <>
          <div className="space-y-4">
            <FormField label={t('tenant.modules.fields.name')} required>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                maxLength={100}
                data-testid="edit-module-name"
              />
            </FormField>

            <FormField label={t('tenant.modules.fields.description')}>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
                maxLength={500}
                data-testid="edit-module-description"
              />
            </FormField>

            <div className="grid grid-cols-2 gap-3">
              <FormField label={t('tenant.modules.fields.icon')}>
                <IconPicker
                  value={form.icon}
                  onChange={(name) => setForm({ ...form, icon: name })}
                  accentColor={form.color}
                />
              </FormField>

              <FormField label={t('tenant.modules.fields.color')}>
                <div className="flex flex-wrap gap-2" data-testid="edit-module-color-picker">
                  {PRESET_COLORS.map((color) => {
                    const selected = form.color === color;
                    return (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setForm({ ...form, color })}
                        aria-label={color}
                        aria-pressed={selected}
                        className={
                          'h-7 w-7 rounded-full transition ' +
                          (selected ? 'ring-2 ring-offset-2 ring-text-primary' : 'hover:scale-110')
                        }
                        style={{ backgroundColor: color }}
                      />
                    );
                  })}
                </div>
              </FormField>
            </div>

            <FormField
              label={t('tenant.modules.fields.recordNameField')}
              hint={t('tenant.modules.editModule.recordNameFieldHint')}
            >
              <Dropdown
                value={form.recordNameField}
                onChange={(v) => setForm({ ...form, recordNameField: v })}
                data-testid="edit-module-record-name-field"
                options={[
                  { value: '', label: t('tenant.modules.editModule.recordNameFieldNone') },
                  ...nameFields.map((f) => ({ value: f.slug, label: `${f.name} (${f.slug})` })),
                ]}
              />
            </FormField>

            {processesActive && (
              <FormField
                label={t('tenant.modules.editModule.defaultProcess')}
                hint={t('tenant.modules.editModule.defaultProcessHint')}
              >
                <Dropdown
                  value={form.defaultProcessId}
                  onChange={(v) => setForm({ ...form, defaultProcessId: v })}
                  data-testid="edit-module-default-process"
                  options={[
                    { value: '', label: t('tenant.modules.editModule.defaultProcessNone') },
                    ...processes.map((p) => ({ value: p.id, label: p.name })),
                  ]}
                />
              </FormField>
            )}

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.showInMenu}
                onChange={(e) => setForm({ ...form, showInMenu: e.target.checked })}
                data-testid="edit-module-show-in-menu"
                className="h-4 w-4 rounded border-border-primary text-accent-600 focus:ring-accent-500"
              />
              <span className="text-text-secondary">{t('tenant.modules.fields.showInMenu')}</span>
            </label>
          </div>

          <div className="mt-6 flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              loading={updateMutation.isPending}
              disabled={!form.name.trim() || updateMutation.isPending}
              data-testid="edit-module-save"
            >
              {t('common.save')}
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
}
